import {useTranslation} from "react-i18next";
import React from "react";
import {FormInput} from "../../FormComponents/Input/Input";
import {Checkbox} from "../../FormComponents/Checkbox";
import DatePicker from '../../Fundamentals/DatePicker';
import SkynetIcon from "../../Fundamentals/SkynetIcon";
import TargetGroupInputTable from "../../FormComponents/TargetGroupInputTable";
import FormSearch, {FormSearchNew} from "../../Search/FormSearch";
import UserSearchNew from "../../Search/UserSearchNew";
import {Dropdown} from "../../FormComponents/Dropdown";
import formhelpers from "../../../Utils/Form/formUtils";
import CustomerSearch from "../../Search/CustomerSearch";

export const fieldRenderers = {
    customerSearch: (field, value, onChange, onBlur ) => {
        return (<FormSearchNew key={field.label} label={field.label}>
                <CustomerSearch
                onChange={value => onChange(field.name, value.customerNumber)}
                defaultSelected={value > 0 ? value : null}
                disabled={field.disabled}
                id={"customerForm"}/>
        </FormSearchNew>
        )},
    userSearch: (field, value, onChange, onBlur) => {
        return (
            <FormSearch key={field.label} label={field.label}>
                {(className) => (
                    <UserSearchNew
                        onChange={(user) => onChange(field.name, user)}
                        defaultSelected={value?.name ?? null}
                        label={field.label}
                        className={className + "pb-0"}
                    />
                )}
            </FormSearch>
        );
    },
    targetGroupInputTable: (field, value, onChange, onBlur) => (
        <TargetGroupInputTable
            key={field.name}
            targetGroups={field.options}
            currentTargetGroups={value}
            onChange={value => onChange(field.name, value)}
        />
    ),
    dropdown: (field, value, onChange, onBlur) => (
        <Dropdown
            key={field.name}
            label={field.label}
            options={field.options}
            placeholder={field.label}
            value={value}
            nameAggregate={field.nameAggregate}
            onChange={value => onChange(field.name, value)}
            disabled={field.disabled}
            error={field.error}
        />
    ),
    button: (field, value, onChange, onBlur) => (
        <div key={field.name} className="form-control skynet-form-input">
            <button className="btn btn-sm mt-6" onClick={e => {
                e.preventDefault();
                onChange(field.name, !value);
            }} value={field.name}>
                {field.label}
                {field.icon && <SkynetIcon icon={field.icon} title={field.label} />}
            </button>
        </div>
    ),
    dateTime: (field, value, onChange, onBlur) => {
        return <DatePicker
            key={field.name}
            dateFormat={"YYYY-MM-DD"} showTime={false}
            onChange={value => onChange(field.name, new Date(Date.parse(value)))}
            initialValue={new Date(Date.parse(value))}
            value={new Date(Date.parse(value))}
            error={field.error}
            label={field.label}
            onBlur={value => onBlur(field)}/>
    },
    checkbox: (field, value, onChange, onBlur) => {
        return <Checkbox
            key={field.name}
            checked={value}
            onChange={value => onChange(field.name, value)}
            label={field.label}
            disabled={field.disabled}/>
    },
    default: (field, value, onChange, onBlur) => {
        return <FormInput
            error={field.error}
            key={field.name}
            label={field.label}
            value={value}
            onChange={value => onChange(field.name, value)}
            type={field.type}
            required={field.required}
            disabled={field.disabled}
            loading={field.loading}
            onBlur={() => onBlur(field)}
        />
    }
};

export const evaluateFieldProperty = (field, propertyName, params) => {
    return field[propertyName] ? field[propertyName](params) : field[propertyName];
};

export const isFieldHidden = (field, params) => evaluateFieldProperty(field, 'isHidden', params);
export const isFieldDisabled = (field, params) => evaluateFieldProperty(field, 'isDisabled', params) || field.disabled;
export const isFieldLoading = (field, params) => evaluateFieldProperty(field, 'isLoading', params) || field.loading;
export const isFieldRequired = (field, params, form) => evaluateFieldProperty(field, 'isRequired', params) || formhelpers.isFieldRequired(form, field);

export function useForm({ form, onChange, onBlur, fieldParams }) {
    const { t } = useTranslation();
    const renderField = (field) => {

        const label = t(field.label);
        const error = form?.fieldErrorFlags?.[field.name];
        const disabled = isFieldDisabled(field, fieldParams);
        const loading = isFieldLoading(field, fieldParams);
        const required = isFieldRequired(field, fieldParams, form);

        const renderer = fieldRenderers[field.type] || fieldRenderers.default;
        
        return renderer({ ...field, label, error, disabled, loading, required }, form.data[field.name], onChange, onBlur);
    };

    return { isFieldHidden, isFieldLoading, isFieldDisabled, isFieldRequired, renderField }
}